/**
 * Keeps Tab / Shift+Tab inside an open modal panel (the slide-out menu), so
 * keyboard focus cannot wander behind the overlay (WCAG 2.4.3).
 *
 * Usage (Alpine 2): <div role="dialog" @keydown.tab="versaTrapFocus($event)">
 */
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]';

window.versaTrapFocus = function (event) {
    const container = event.currentTarget;
    const items = Array.from(container.querySelectorAll(FOCUSABLE))
        .filter((el) => el.tabIndex >= 0 && el.getClientRects().length > 0 && !el.closest('[inert]'));

    if (!items.length) return;

    const first = items[0];
    const last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
    }
};

/**
 * Moves focus to the first focusable element inside `selector` (e.g. the mini
 * cart when it opens). `[data-dialog-initial]` wins if present.
 */
window.versaFocusFirst = function (selector) {
    const container = document.querySelector(selector);
    if (!container) return;
    const target = container.querySelector('[data-dialog-initial]')
        || Array.from(container.querySelectorAll(FOCUSABLE)).find((el) => el.tabIndex >= 0 && el.getClientRects().length > 0);
    if (target) target.focus({ preventScroll: true });
};

/**
 * select2 (used by the Aelia country selector) labels its combobox with the
 * selected value only ("Sverige"). Prefix the real label from the widget
 * template so screen readers hear e.g. "Inställt land, Sverige" (WCAG 1.3.1/4.1.2).
 */
function labelSelect2Comboboxes() {
    document.querySelectorAll('select[data-versa-label]').forEach((select) => {
        const container = select.nextElementSibling;
        if (!container || !container.classList.contains('select2-container')) return;
        const combo = container.querySelector('.select2-selection[role="combobox"]');
        if (!combo) return;
        const labelId = select.getAttribute('data-versa-label');
        const ids = (combo.getAttribute('aria-labelledby') || '').split(/\s+/).filter(Boolean);
        if (!ids.includes(labelId)) combo.setAttribute('aria-labelledby', [labelId, ...ids].join(' '));
    });
}

// select2 is initialised by the Aelia plugin on DOM ready; run after it.
if (window.jQuery) {
    window.jQuery(() => setTimeout(labelSelect2Comboboxes, 0));
}
window.addEventListener('load', labelSelect2Comboboxes);

/**
 * Polite screen-reader announcements for status changes that are otherwise
 * only visual, e.g. the "Tillagd i varukorgen" toast (WCAG 4.1.3). The live
 * region exists from page load; regions created on demand are often missed.
 */
function ensureLiveRegion() {
    let live = document.getElementById('versa-live');
    if (!live && document.body) {
        live = document.createElement('div');
        live.id = 'versa-live';
        live.className = 'sr-only';
        live.setAttribute('role', 'status');
        live.setAttribute('aria-live', 'polite');
        document.body.appendChild(live);
    }
    return live;
}

window.versaAnnounce = function (text) {
    const live = ensureLiveRegion();
    if (!live) return;
    live.textContent = '';
    window.setTimeout(() => { live.textContent = text; }, 100);
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ensureLiveRegion);
} else {
    ensureLiveRegion();
}

/**
 * Select-only combobox (ARIA APG) for the PDP variation picker. Focus stays on
 * the combobox; the highlighted option is exposed via aria-activedescendant.
 * Selecting clicks the option, so its existing Alpine handler does the work.
 *
 * Usage (Alpine 2): <div role="combobox" aria-controls="listbox-id"
 *   @click="open = !open" @keydown="versaSelectKeydown($event)">
 */
window.versaSelectKeydown = function (event) {
    const combo = event.currentTarget;
    const list = document.getElementById(combo.getAttribute('aria-controls'));
    if (!list) return;
    const options = Array.from(list.querySelectorAll('[role="option"]'));
    if (!options.length) return;
    const expanded = combo.getAttribute('aria-expanded') === 'true';

    let index = options.findIndex((o) => o.id === combo.getAttribute('aria-activedescendant'));
    if (index < 0) index = Math.max(0, options.findIndex((o) => o.getAttribute('aria-selected') === 'true'));

    const highlight = (i) => {
        options.forEach((o) => o.classList.remove('is-active'));
        const option = options[i];
        option.classList.add('is-active');
        combo.setAttribute('aria-activedescendant', option.id);
        option.scrollIntoView({ block: 'nearest' });
    };
    const clear = () => {
        options.forEach((o) => o.classList.remove('is-active'));
        combo.removeAttribute('aria-activedescendant');
    };
    // Opening goes through the combobox's own click handler (Alpine `open`);
    // highlight once the list has rendered.
    const open = (i) => {
        combo.click();
        window.requestAnimationFrame(() => highlight(i));
    };

    switch (event.key) {
        case 'ArrowDown':
        case 'ArrowUp': {
            event.preventDefault();
            const step = event.key === 'ArrowDown' ? 1 : -1;
            if (!expanded) { open(index); return; }
            highlight(Math.min(options.length - 1, Math.max(0, index + step)));
            return;
        }
        case 'Home':
        case 'End':
            if (!expanded) return;
            event.preventDefault();
            highlight(event.key === 'Home' ? 0 : options.length - 1);
            return;
        case 'Enter':
        case ' ':
            event.preventDefault();
            if (!expanded) { open(index); return; }
            clear();
            options[index].click();
            return;
        case 'Escape':
            if (!expanded) return;
            event.preventDefault();
            clear();
            combo.click();
            return;
        case 'Tab':
            if (expanded) { clear(); combo.click(); }
            return;
        default:
    }
};

/**
 * ARIA tabs keyboard support (roving tabindex is bound in the template):
 * arrow keys / Home / End move to and select a tab (WCAG 2.1.1).
 * Usage (Alpine 2): <div role="tablist" @keydown="versaTabsKeydown($event)">
 */
window.versaTabsKeydown = function (event) {
    const tabs = Array.from(event.currentTarget.querySelectorAll('[role="tab"]'));
    const current = tabs.indexOf(document.activeElement);
    if (current < 0) return;
    let next = null;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (current + 1) % tabs.length;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (current - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    if (next === null) return;
    event.preventDefault();
    tabs[next].focus();
    tabs[next].click();
};

/**
 * Variant tile tooltips (pdp buy box) show on hover and keyboard focus. Esc
 * hides them until the pointer or focus moves to something else (WCAG 1.4.13).
 */
document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') document.documentElement.classList.add('versa-tips-off');
});
['mouseover', 'focusin'].forEach((type) => {
    document.addEventListener(type, () => document.documentElement.classList.remove('versa-tips-off'), { passive: true });
});
